/**
 * Shared visual vocabulary for the daily ritual (plan / EOD / retro-EOD).
 *
 * The MUI theme in this app carries almost no component overrides — the visual
 * language lives in page-level `sx`. These factories keep the three ritual
 * surfaces speaking that same language without the two panels drifting apart.
 */

import { Theme } from '@mui/material';

export type RitualTone = 'plan' | 'eod' | 'retro';

/** Which way a choice leans — drives the card tint and the selected pill color. */
export type DecisionTone = 'neutral' | 'positive' | 'negative';

interface ToneSpec {
  /** Full-bleed header gradient. 135deg is the app-wide gradient angle. */
  gradient: { light: string; dark: string };
  /** Palette key for the primary CTA. Green = punch-in, red = punch-out,
   *  indigo = transitional (retro leads straight into the plan). */
  accent: 'success' | 'error' | 'primary';
}

export const RITUAL_TONES: Record<RitualTone, ToneSpec> = {
  plan: {
    gradient: {
      light: 'linear-gradient(135deg, #10b981 0%, #4f46e5 100%)',
      dark: 'linear-gradient(135deg, #064e3b 0%, #1e3a8a 100%)'
    },
    accent: 'success'
  },
  eod: {
    gradient: {
      // Amber-600/red-600, not amber-500/red-500: white text on #f59e0b is ~2:1
      // contrast (a WCAG failure), and the header text here is always white.
      light: 'linear-gradient(135deg, #d97706 0%, #dc2626 100%)',
      dark: 'linear-gradient(135deg, #78350f 0%, #7f1d1d 100%)'
    },
    accent: 'error'
  },
  retro: {
    // Already dark-toned; identical in both modes. Deliberately not red —
    // catching up on a missed day shouldn't read as a punishment.
    gradient: {
      light: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)',
      dark: 'linear-gradient(135deg, #334155 0%, #1e293b 100%)'
    },
    accent: 'primary'
  }
};

export const toneGradient = (tone: RitualTone, isDark: boolean): string =>
  isDark ? RITUAL_TONES[tone].gradient.dark : RITUAL_TONES[tone].gradient.light;

/**
 * A task card in each of its decision states. Uses the codebase's `${color}15`
 * hex-alpha tint idiom plus a 4px left accent bar so state is legible at a glance
 * without relying on color alone (the pill label always says it too).
 */
export const taskCardSx = (theme: Theme, isDark: boolean, state: DecisionTone) => {
  const positive = theme.palette.success.main;
  const negative = theme.palette.warning.main;

  const borderColor =
    state === 'negative' ? negative : state === 'positive' ? `${positive}55` : theme.palette.divider;

  const background =
    state === 'negative'
      ? `linear-gradient(135deg, ${negative}15 0%, ${theme.palette.background.paper} 100%)`
      : state === 'positive'
        ? `linear-gradient(135deg, ${positive}12 0%, ${theme.palette.background.paper} 100%)`
        : isDark
          ? 'rgba(255,255,255,0.03)'
          : theme.palette.background.paper;

  const accent =
    state === 'negative' ? negative : state === 'positive' ? positive : theme.palette.divider;

  return {
    p: 2,
    mb: 1.5,
    borderRadius: 2,
    border: '1px solid',
    borderColor,
    background,
    borderLeft: '4px solid',
    borderLeftColor: accent,
    transition: 'all 0.25s ease'
  } as const;
};

/**
 * Pill-track segmented control. Replaces the bare outlined ToggleButton pairs —
 * same MUI component, far more visual weight.
 *
 * Selected colors are driven by a `data-tone` attribute on each ToggleButton
 * rather than by value, so the same styles serve continue/drop and
 * completed/not-completed.
 */
export const segmentedGroupSx = (isDark: boolean) =>
  ({
    mt: 1.5,
    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'grey.100',
    borderRadius: 2,
    p: 0.5,
    gap: 0.5,
    '& .MuiToggleButton-root': {
      border: 'none',
      borderRadius: '8px !important',
      px: 2,
      py: 0.75,
      fontWeight: 600,
      fontSize: '0.8125rem',
      textTransform: 'none',
      color: 'text.secondary',
      transition: 'all 0.2s ease',
      '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' }
    },
    '& .MuiToggleButton-root[data-tone="positive"].Mui-selected': {
      bgcolor: 'success.main',
      color: '#fff',
      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      '&:hover': { bgcolor: 'success.dark' }
    },
    '& .MuiToggleButton-root[data-tone="negative"].Mui-selected': {
      bgcolor: 'warning.main',
      color: '#fff',
      boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
      '&:hover': { bgcolor: 'warning.dark' }
    }
  }) as const;

/** The app's established primary-CTA idiom: pill-ish, lifts on hover, colored glow. */
export const ctaSx = (theme: Theme, accent: 'success' | 'error' | 'primary') => {
  const main = theme.palette[accent].main;
  return {
    px: { xs: 3, sm: 5 },
    py: 1.25,
    borderRadius: 2,
    fontWeight: 600,
    fontSize: '0.95rem',
    textTransform: 'none',
    bgcolor: main,
    boxShadow: `0 4px 12px ${main}4d`,
    '&:hover': {
      bgcolor: theme.palette[accent].dark,
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 16px ${main}66`
    },
    '&.Mui-disabled': { bgcolor: main, opacity: 0.4, color: '#fff' },
    transition: 'all 0.25s ease'
  } as const;
};

/** 'YYYY-MM-DD' -> 'Sat, 11 Jul'. Parsed at noon so the label can't slip a day. */
export const formatPlanDate = (planDate?: string | null): string => {
  if (!planDate) return '';
  const d = new Date(`${planDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return planDate;
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
};

/** 'YYYY-MM-DD' -> 'Saturday, 11 July'. For headings, where the long form reads better. */
export const formatPlanDateLong = (planDate?: string | null): string => {
  if (!planDate) return '';
  const d = new Date(`${planDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return planDate;
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
};

/** Time-aware greeting. Blocking mode can open any time the window is open. */
export const greeting = (name?: string): string => {
  const h = new Date().getHours();
  const part = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  return name ? `${part}, ${name}.` : `${part}.`;
};
